export interface DocumentAnalysis {
  success: boolean;
  analysis: {
    image_quality: {
      resolution: string;
      sharpness_score: number;
      brightness_score: number;
      contrast_score: number;
      is_legible: boolean;
    };
    document_type: string;
    recommendations: string[];
    requires_back_side: boolean;
    faces_detected: {
      count: number;
      faces_found: boolean;
      face_locations: number[][];
    };
  };
  error?: string;
}

export interface FacialVerification {
  success: boolean;
  verification: {
    facial_analysis: {
      success: boolean;
      duration: number;
      frame_count: number;
      fps: number;
      face_detection_rate: number;
      has_consistent_face: boolean;
      quality_score: number;
    };
    movement_analysis: {
      success: boolean;
      duration: number;
      movement_detected: boolean;
      movement_sequence: string[];
      movement_analysis: {
        is_valid: boolean;
        has_left_movement: boolean;
        has_right_movement: boolean;
        has_center_movement: boolean;
        transitions: string[];
        sequence_length: number;
      };
      quality_score: number;
    };
    face_comparison: {
      success: boolean;
      face_match_score: number;
      is_same_person: boolean;
      confidence: number;
    } | null;
    overall_verification: {
      verified: boolean;
      confidence: number;
      criteria: {
        facial_quality: boolean;
        movement_quality: boolean;
        consistent_face: boolean;
        valid_movements: boolean;
        face_match: boolean;
      };
    };
  };
  error?: string;
}

export interface KYCSubmissionResult {
  success: boolean;
  kyc_id: string;
  submitted_at: string;
  documents: {
    [key: string]: {
      filename: string;
      size: number;
      content_type: string;
      path: string;
    };
  };
  analyses: {
    [key: string]: DocumentAnalysis;
  };
  overall_status: {
    status: 'approved' | 'pending_review' | 'rejected' | 'incomplete';
    reason: string;
    confidence: number;
    documents_submitted: number;
    successful_analyses: number;
    total_analyses: number;
  };
  message: string;
  error?: string;
}

export type DocumentType = 'bi' | 'cc' | 'passport';

class KYCApiService {
  private baseUrl: string;

  constructor(baseUrl: string = '/api/kyc') {
    this.baseUrl = baseUrl;
  }

  /**
   * Analyzes a document photo for quality and characteristics
   */
  async analyzeDocument(photo: File, documentType: DocumentType): Promise<DocumentAnalysis> {
    const formData = new FormData();
    formData.append('photo', photo);
    formData.append('document_type', documentType);

    try {
      const response = await fetch(`${this.baseUrl}/analyze-document-photo/`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json()
      return result
    } catch (error) {
      return {
        success: false,
        analysis: {
          image_quality: {
            resolution: '0x0',
            sharpness_score: 0,
            brightness_score: 0,
            contrast_score: 0,
            is_legible: false,
          },
          document_type: documentType,
          recommendations: ['Erro na análise da imagem'],
          requires_back_side: true,
          faces_detected: {
            count: 0,
            faces_found: false,
            face_locations: [],
          },
        },
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Verifies facial videos and compares with document photo
   */
  async verifyFacialVideos(
    facialVideo: File,
    movementVideo: File,
    documentPhoto?: File
  ): Promise<FacialVerification> {
    const formData = new FormData();
    formData.append('facial_video', facialVideo);
    formData.append('movement_video', movementVideo);
    
    if (documentPhoto) {
      formData.append('document_photo', documentPhoto);
    }

    try {
      const response = await fetch(`${this.baseUrl}/verify-facial-videos/`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json()
      return result
    } catch (error) {
      return {
        success: false,
        verification: {
          facial_analysis: {
            success: false,
            duration: 0,
            frame_count: 0,
            fps: 0,
            face_detection_rate: 0,
            has_consistent_face: false,
            quality_score: 0,
          },
          movement_analysis: {
            success: false,
            duration: 0,
            movement_detected: false,
            movement_sequence: [],
            movement_analysis: {
              is_valid: false,
              has_left_movement: false,
              has_right_movement: false,
              has_center_movement: false,
              transitions: [],
              sequence_length: 0,
            },
            quality_score: 0,
          },
          face_comparison: null,
          overall_verification: {
            verified: false,
            confidence: 0,
            criteria: {
              facial_quality: false,
              movement_quality: false,
              consistent_face: false,
              valid_movements: false,
              face_match: false,
            },
          },
        },
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Validates image quality based on API criteria
   */
  validateImageQuality(analysis: DocumentAnalysis['analysis']): {
    isValid: boolean;
    issues: string[];
    recommendations: string[];
  } {
    const issues: string[] = [];
    const recommendations: string[] = [];

    const { image_quality } = analysis;

    // Check sharpness (should be > 0.01)
    if (image_quality.sharpness_score <= 0.01) {
      issues.push('Imagem muito desfocada');
      recommendations.push('Mantenha o dispositivo estável e focado no documento');
    }

    // Check brightness (should be between 50 and 200)
    if (image_quality.brightness_score < 50) {
      issues.push('Imagem muito escura');
      recommendations.push('Melhore a iluminação do ambiente');
    } else if (image_quality.brightness_score > 200) {
      issues.push('Imagem muito clara');
      recommendations.push('Evite reflexos e luzes muito intensas');
    }

    // Check contrast (should be > 30)
    if (image_quality.contrast_score <= 30) {
      issues.push('Contraste insuficiente');
      recommendations.push('Certifique-se de que o documento está bem contrastado');
    }

    return {
      isValid: image_quality.is_legible && issues.length === 0,
      issues,
      recommendations: recommendations.length > 0 ? recommendations : analysis.recommendations,
    };
  }

  /**
   * Validates facial verification results
   */
  validateFacialVerification(verification: FacialVerification['verification']): {
    isValid: boolean;
    issues: string[];
    confidence: number;
  } {
    const issues: string[] = [];
    const { overall_verification, facial_analysis, movement_analysis } = verification;

    // Check facial quality
    if (!overall_verification.criteria.facial_quality) {
      issues.push('Qualidade do vídeo facial insuficiente');
    }

    // Check movement quality
    if (!overall_verification.criteria.movement_quality) {
      issues.push('Qualidade dos movimentos insuficiente');
    }

    // Check consistent face
    if (!overall_verification.criteria.consistent_face) {
      issues.push('Rosto não detectado consistentemente');
    }

    // Check valid movements
    if (!overall_verification.criteria.valid_movements) {
      issues.push('Movimentos laterais inválidos');
    }

    // Check face match (if document photo provided)
    if (overall_verification.criteria.face_match === false) {
      issues.push('Rosto não corresponde ao documento');
    }

    return {
      isValid: overall_verification.verified,
      issues,
      confidence: overall_verification.confidence,
    };
  }

  /**
   * Submits complete KYC data to the backend
   */
  async submitKYCData(
    documents: Record<string, File>,
    facialVideo?: File,
    movementVideo?: File
  ): Promise<KYCSubmissionResult> {
    const formData = new FormData();
    
    // Add documents to form data
    Object.entries(documents).forEach(([docType, file]) => {
      if (file) {
        const fieldName = docType.endsWith('_document') ? docType : `${docType}_document`;
        formData.append(fieldName, file);
      }
    });

    // Add facial video if provided
    if (facialVideo) {
      formData.append('facial_video', facialVideo);
    }

    // Add movement video if provided
    if (movementVideo) {
      formData.append('movement_video', movementVideo);
    }

    try {
      const response = await fetch(`${this.baseUrl}/submit-kyc/`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('KYC submission failed:', errorData);
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ KYC submission error:', error);
      return {
        success: false,
        kyc_id: '',
        submitted_at: new Date().toISOString(),
        documents: {},
        analyses: {},
        overall_status: {
          status: 'incomplete',
          reason: 'Error submitting KYC data',
          confidence: 0,
          documents_submitted: 0,
          successful_analyses: 0,
          total_analyses: 0,
        },
        message: 'Failed to submit KYC data',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}

export const kycApiService = new KYCApiService();
export default KYCApiService;

